import { AgGridReact } from "ag-grid-react";
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CellClickedEvent,
  ColDef,
  ExcelExportParams,
  GridApi,
  GridOptions,
  SideBarDef,
  ValueParserParams,
} from "ag-grid-community";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import { Grant } from "../../types/Grant";

import {
  Box,
  HStack,
  IconCTAButton,
  VStack,
  isAdminViewer,
  isEsopViewer,
} from "../../components/utils";
import { useDeleteGrant } from "../../queries/esopPlan";
import { useAuthorizationStore, useCompanyStore } from "../../store";
import { TriggerType } from "../../types/VestingTemplate";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import {
  capitalizeFirstLetter,
  getFormattedValue,
  limitString,
  stringFormat,
  stringToNumberConvertor,
} from "../../utils/string";
import Avatar from "./Avatar";
import { isAccelerationAllowed, isExerciseAllowed } from "./GrantsTable";
import GrantStatusLabel from "./StatusLabel";

import SearchInput from "../../components/shared/SearchInput";
import { usePermissionStore } from "../../store/permissionStore";
import { determineUserAccessToResource } from "../../utils/auth";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import MyCustomFilters from "../../components/shared/MyCustomFilters";
import { AvailableTableFilters, SaveTableState } from "../../types/Table";
import { saveTableFilterState } from "../../api/gridUtils";
import { useGetFilterStates } from "../../queries/gridUtils";
import Tooltip from "../../components/shared/Tooltip";
import { useTableStateManagement } from "../../components/shared/TableHook";
import { TableId } from "../../constants/TableIdConstants";
import CloudSetting from "../../components/shared/CloudIcon";
import StatusLabelColors from "../../constants/StatusLabelColors";
import { formatDisplayDate } from "../../utils/date";
import AdvancedAgGridSearch from "../../components/AdvancedAgGridSearch";
import { ConversionMethodology } from "../../types/Exercise";

type GrantInfoCellProps = {
  grant: Grant;
  currency: string; // assuming you have a currency prop
};
function AgGridGrantsTable({
  grantTableData,
  handleAction,
  onSelectedRows,
  gridRef,
  tableFilterCount,
}: {
  grantTableData: Grant[];
  handleAction: (grant: Grant, action: Action) => void;
  onSelectedRows: (item: Grant[]) => void;
  gridRef: any;
  tableFilterCount: Function;
}) {
  grantTableData.sort(
    (a, b) => new Date(b.grantDate).getTime() - new Date(a.grantDate).getTime()
  );
  const { id } = useParams();
  const currency = getCurrencyType();
  const currencySymbol = getCurrencySymbol();
  const userPermission = useAuthorizationStore();
  const { companyData } = useCompanyStore();
  const showValueAtGrantToAdmin = Boolean(
    companyData?.companySettings?.grantLetterSettings?.showValueAtGrantToAdmin
  );
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const isUserAdminViewer = isAdminViewer();
  const isUserEsopViewer = isEsopViewer();
  const { mutate: deleteGrant } = useDeleteGrant();
  const { permission } = usePermissionStore();
  const { data, refetch } = useGetFilterStates("PLANSANDGRANTS");
  // route params logic
  const isPlaceholderData = false;
  const navigate = useNavigate();
  const tableData: Grant[] = useMemo(() => grantTableData, [grantTableData]);
  const gridOptions: GridOptions = {};
  const gridApi = useRef<GridApi | any>(null);
  const chartThemes = useMemo<string[]>(() => ["ag-pastel", "ag-vivid"], []);
  const exportParams: ExcelExportParams = {
    processCellCallback: (params) => {
      // Customize cell values during export
      if (typeof params.value === "number") {
        return params.value.toFixed(2); // Format numbers to 2 decimal places
      }
      return params.value;
    },
  };

  const componentsRegistery = useMemo(
    () => ({
      holderInfo: memo(HolderInfoRender),
    }),
    []
  );

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
    }),
    []
  );
  const filterparams = {
    filterParams: {
      filterOptions: [
        "lessThan",
        {
          displayKey: "lessThanWithNulls",
          displayName: "Less Than with Nulls",
          predicate: ([filterValue]: any, cellValue: number | null) =>
            cellValue == null || cellValue < filterValue,
        },
        "greaterThan",
        {
          displayKey: "greaterThanWithNulls",
          displayName: "Greater Than with Nulls",
          predicate: ([filterValue]: any, cellValue: number | null) =>
            cellValue == null || cellValue > filterValue,
        },
        {
          displayKey: "betweenExclusive",
          displayName: "Between (Exclusive)",
          predicate: ([fv1, fv2]: any, cellValue: number | null) =>
            cellValue == null ||
            // eslint-disable-next-line no-mixed-operators
            (fv1 < cellValue && fv2 > cellValue),
          numberOfInputs: 2,
        },
      ],
    },
  };
  const TagsCell = (params: any) => {
    const tags: { label: string; value: string }[] = params.value || [];
    if (!tags.length) return null;
    return (
      <div className="flex flex-wrap gap-1">
        {tags.map((tag) => (
          <span
            key={tag.value}
            className="inline-block px-2 py-1 text-xs bg-gray-200 rounded"
          >
            {tag.label}
          </span>
        ))}
      </div>
    );
  };
  const isValidDate = (date: string) =>
    Number.isNaN(date) &&
    new Date(date).toString() !== "Invalid Date" &&
    !Number.isNaN(Date.parse(date));
  const normaliseValue = (value: string | number) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      // check if it can be converted to number first
      // assume if commas are there
      if (isValidDate(value)) return new Date(value).valueOf();
      try {
        value = value.replaceAll(",", "");
      } catch (TypeError) {
        value = value.replace(/,./g, "");
      }
      return parseFloat(value);
    }
    return value;
  };
  const sortComparator = (
    valueA: number | string,
    valueB: number | string,
    ..._: any[]
  ) => normaliseValue(valueA) - normaliseValue(valueB);

  const handleCellClick = (cellParams: CellClickedEvent<any, any>) => {
    if (cellParams.column.getColId() !== "action-column") {
      const rowData = grantTableData.find(
        (row) =>
          row.employeeId ===
          cellParams?.data?.grantInfo?.props?.grant?.employeeId
      );
      if (rowData) {
        navigate(`/options/employeeDetails/${rowData.employeeId}`);
      }
    }
  };

  function numberValueParser(params: ValueParserParams) {
    const res = Number.parseInt(params.newValue, 10);
    if (Number.isNaN(res)) {
      return undefined;
    }
    return res;
  }

  const handleSelectionChanged = (item: any) => {
    const selectedRows = item.api.getSelectedRows();
    const grantsSelected = selectedRows.map(
      (row: any) => row.grantInfo.props.grant
    );

    onSelectedRows(grantsSelected);
  };
  const {
    filterCondition,
    setFilterCondition,
    isSaving,
    uploadFilter,
    uploadColumn,
    tableState,
  } = useTableStateManagement(gridApi, TableId.grantTable, id);
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      comparator: sortComparator,
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );
  function applyRoundingComponent({
    value,
    methodology,
  }: {
    value: number;
    methodology: string;
  }) {
    const roundingValue =
      methodology === ConversionMethodology.ROUND_DOWN
        ? Math.floor(value)
        : Math.ceil(value);

    const valueIsDecimal = value % 1 !== 0;

    return (
      <VStack className=" items-center justify-center p-4">
        <p className="text-xs font-normal text-gray-dark ">
          {roundingValue.toLocaleString(currency)}
        </p>

        {valueIsDecimal && (
          <p className="text-xxs text-gray-light">
            ({Number(value.toFixed(4)).toLocaleString(currency)})
          </p>
        )}
      </VStack>
    );
  }
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "HOLDER'S NAME",
        checkboxSelection: true,
        width: 200,
        minwidth: 350,
        autoHeight: true,
        wrapText: false,
        headerCheckboxSelection: true,
        cellStyle: { "padding-top": "15px" },
        field: "holderName",
        filter: "agMultiColumnFilter",

        getQuickFilterText: (params) => {
          const { optionHolderName, employeeIdentifier } = params?.value || {};

          const valuesToPass = [optionHolderName, employeeIdentifier].filter(
            (value) => value !== undefined && value !== null
          );

          return valuesToPass.toString();
        },
        suppressSizeToFit: true,
        initialWidth: 200,
        minWidth: 200,
        sortable: true,
        comparator(valueA: any, valueB: any, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1;
        },
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Holder Name",
              display: "subMenu",
              filter: "agSetColumnFilter",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                keyCreator: (params: any) => {
                  const eventName = params.value.optionHolderName;
                  return eventName;
                },
                valueFormatter: (params: any) => {
                  const eventName = params.value.optionHolderName;

                  return eventName;
                },
              },
            },
            {
              title: "Employee Id",
              filter: "agSetColumnFilter",
              display: "subMenu",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                comparator: sortComparator,

                keyCreator: (params: any) => {
                  const eventName = params.value.employeeIdentifier;
                  return eventName;
                },
                valueFormatter: (params: any) => {
                  const eventName = params.value.employeeIdentifier;

                  return eventName;
                },
              },
            },
            {
              title: "Employment Status",
              filter: "agSetColumnFilter",
              display: "subMenu",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                comparator: sortComparator,
                keyCreator: (params: any) => {
                  const eventName = params.value.statusId;
                  return eventName;
                },
                valueFormatter: (params: any) => {
                  const eventName = params.value.statusId;
                  return eventName;
                },
              },
            },
          ],
        },
        menuTabs: ["filterMenuTab"],
        cellRendererParams: ({ value }: { value: { props: any } }) =>
          value ? value.props : null,
        cellRenderer: "holderInfo",
      },
      {
        headerName: "GRANT INFO",
        field: "grantInfo",
        sortable: true,
        autoHeight: true,
        wrapText: true,
        minWidth: 200,
        width: 200,
        filter: "agMultiColumnFilter",
        suppressAutoSize: false,
        getQuickFilterText: (params) => {
          const {
            optionHolderState,
            grantDate,
            grantPrice,
            grantIdentifier,
            isPausedVesting,
            pausedVestingDate,
          } = params?.data?.grantInfo?.props?.grant || {};

          const valuesToPass = [
            optionHolderState,
            grantDate,
            grantPrice,
            grantIdentifier,
            isPausedVesting
              ? `Paused On ${formatDisplayDate(pausedVestingDate)}`
              : "",
          ].filter((value) => value !== undefined && value !== null);

          return valuesToPass.toString();
        },
        comparator(valueA, valueB, ..._other) {
          return valueA.props.grant.grantIdentifier?.localeCompare(
            valueB.props.grant.grantIdentifier
          );
        },
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Grant Id",
              display: "subMenu",
              filter: "agSetColumnFilter",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                keyCreator: (params: any) => {
                  const eventName = params.value.props.grant.grantIdentifier;

                  return eventName;
                },
                valueFormatter: (params: any) => {
                  const eventName = params.value.props.grant.grantIdentifier;

                  return eventName;
                },
              },
            },
            {
              title: "Grant Date",
              display: "subMenu",
              filterParams: {
                maxNumConditions: 5,
                inRangeInclusive: true,
                buttons: ["reset"],
                comparator: (
                  filterLocalDateAtMidnight: Date,
                  cellValue: { props: GrantInfoCellProps }
                ) => {
                  if (!cellValue || !cellValue.props?.grant?.grantDate) {
                    return -1;
                  }

                  const filterDate = new Date(filterLocalDateAtMidnight);
                  filterDate.setHours(0, 0, 0, 0);

                  const cellDate = new Date(cellValue.props.grant.grantDate);
                  cellDate.setHours(0, 0, 0, 0);

                  const filterEpoch = filterDate.getTime();
                  const cellEpoch = cellDate.getTime();

                  if (cellEpoch < filterEpoch) {
                    return -1;
                  } else if (cellEpoch > filterEpoch) {
                    return 1;
                  }
                  return 0;
                },
              },
              filter: "agDateColumnFilter",
            },
            {
              title: "Grant Price",
              display: "subMenu",
              filter: "agNumberColumnFilter",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                valueGetter: (params: any) => {
                  const grantPrice =
                    params.data.grantInfo.props.grant.grantPrice;
                  return grantPrice;
                },
              },
            },
            {
              title: "Pause Vesting",
              display: "subMenu",
              filter: "agSetColumnFilter",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                keyCreator: (params: any) => {
                  const grant = params.value.props.grant;
                  const value = grant.isPausedVesting
                    ? `on ${formatDisplayDate(grant.pausedVestingDate)}`
                    : "Not Paused";
                  return value;
                },
                valueFormatter: (params: any) => {
                  const grant = params.value.props.grant;
                  const value = grant.isPausedVesting
                    ? `on ${formatDisplayDate(grant.pausedVestingDate)}`
                    : "Not Paused";
                  return value;
                },
              },
            },
          ],
        },
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
        cellStyle: { "padding-top": "15px" },
        cellRendererParams: ({ value }: any) => (value ? value.props : null),
        cellRenderer: GrantInfoCell,
      },
      {
        headerName: "GRANT STATUS",
        field: "grantStatus",
        autoHeight: true,
        wrapText: true,
        minWidth: 180,
        filter: "agSetColumnFilter",

        filterParams: {
          suppressSelectAll: false,
          suppressMiniFilter: false,
          buttons: ["reset"],
        },
        menuTabs: ["filterMenuTab"],
        sortable: true,
        comparator(valueA, valueB, ..._other) {
          return valueA.localeCompare(valueB);
        },
        cellRenderer: GrantStatus,
      },

      {
        headerName: "OPTIONS GRANTED",
        field: "optionsGranted",
        autoHeight: true,
        wrapText: true,
        cellStyle: { "padding-top": "15px" },

        getQuickFilterText: (params) => {
          const { optionsGranted, planName } =
            params?.data?.grantInfo?.props?.grant || {};

          const valuesToPass = [optionsGranted, planName].filter(
            (value) => value !== undefined && value !== null
          );

          return valuesToPass.toString();
        },
        filter: "agMultiColumnFilter",
        comparator(valueA, valueB, nodeA, nodeB, isDescending) {
          return sortComparator(
            valueA.props.optionsGranted,
            valueB.props.optionsGranted,
            nodeA,
            nodeB,
            isDescending
          );
        },
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Plan Name",
              filter: "agSetColumnFilter",
              display: "subMenu",
              filterParams: {
                suppressSelectAll: false,
                suppressMiniFilter: false,
                buttons: ["reset"],
                keyCreator: (params: any) =>
                  params?.data?.grantInfo?.props?.grant?.planName,
                valueFormatter: (params: any) => params?.value?.props?.planName,
              },
            },
            {
              title: "Options Granted",
              display: "subMenu",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) =>
                  params.data.grantInfo.props.grant.optionsGranted,
              },
              filter: "agNumberColumnFilter",
            },
          ],
        },
        sortable: true,
        menuTabs: ["filterMenuTab"],
        cellRendererParams: ({ value }: { value: { props: any } }) =>
          value ? value.props : null,
        cellRenderer: OptionInfoCell,
      },
      ...(showValueAtGrantToAdmin
        ? [
            {
              headerName: "VALUE AT GRANT",
              field: "valueAtGrant",
              autoHeight: true,
              wrapText: true,
              cellStyle: { "padding-top": "15px" },
              filter: "agNumberColumnFilter",
              filterParams: filterparams,
              menuTabs: ["filterMenuTab"],
              sortable: true,
              cellRenderer: (params: any) => (
                <ValueAtGrantCell
                  sharePrice={params.data.valueAtGrant}
                  grantPrice={params.data.pricePerOption}
                  currency={currency}
                />
              ),
            } as ColDef,
          ]
        : []),

      {
        headerName: "Equivalent Shares (Granted)".toUpperCase(),
        field: "equivalentSharesGranted",
        autoHeight: true,
        wrapText: true,
        cellRenderer: (params: any) =>
          applyRoundingComponent({
            value: params.data.equivalentSharesGranted || 0,
            methodology: params.data.conversionMethodology || "ROUND_DOWN",
          }),
        valueParser: numberValueParser,
        filter: "agNumberColumnFilter",
        filterValueGetter: (params) =>
          stringToNumberConvertor(params.getValue("equivalentSharesGranted")),
        filterParams: filterparams,
        menuTabs: ["filterMenuTab"],
        sortable: true,
        hide: true,
      },

      {
        headerName: "VESTED",
        field: "vested",
        autoHeight: true,
        wrapText: true,
        sortable: true,
        filter: "agMultiColumnFilter",
        comparator(valueA, valueB, nodeA, nodeB, isDescending) {
          return sortComparator(
            valueA.props.optionsVested,
            valueB.props.optionsVested,
            nodeA,
            nodeB,
            isDescending
          );
        },
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Options Vested",
              display: "subMenu",
              filterValueGetter: (params: any) =>
                stringToNumberConvertor(
                  params?.data.grantInfo.props.grant.optionsVested
                ),
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) =>
                  params?.data.grantInfo.props.grant.optionsVested,
              },
              filter: "agNumberColumnFilter",
            },
            {
              title: "Vesting Template",
              filter: "agSetColumnFilter",
              display: "subMenu",
              filterParams: {
                suppressSelectAll: false,
                suppressMiniFilter: false,
                buttons: ["reset"],
                keyCreator: (params: any) => {
                  const status =
                    params?.data?.grantInfo?.props?.grant?.vestingTemplateName;
                  return status; // Custom keyCreator logic
                },
                valueFormatter: (params: any) => {
                  const status = params?.value?.props?.vestingTemplateName;
                  return status;
                },
              },
            },
          ],
        },
        menuTabs: ["filterMenuTab"],
        cellRenderer: GrantDetailsCell,
        cellRendererParams: ({ value }: { value: { props: any } }) =>
          value ? value.props : null,
      },
      {
        headerName: "UNVESTED",
        field: "unvested",
        autoHeight: true,
        wrapText: true,
        valueParser: numberValueParser,
        filter: "agNumberColumnFilter",
        filterValueGetter: (params) =>
          stringToNumberConvertor(params.getValue("unvested")),
        filterParams: filterparams,
        menuTabs: ["filterMenuTab"],
        sortable: true,
      },
      {
        headerName: "NET VESTED",
        field: "netVested",
        autoHeight: true,
        wrapText: true,
        filter: "agNumberColumnFilter",
        filterValueGetter: (params) =>
          stringToNumberConvertor(params.getValue("netVested")),
        filterParams: filterparams,
        menuTabs: ["filterMenuTab"],
        sortable: true,
      },
      {
        headerName: "STOCK EXERCISED",
        field: "stockExercised",
        autoHeight: true,
        wrapText: true,
        filter: "agNumberColumnFilter",
        filterValueGetter: (params) =>
          stringToNumberConvertor(params.getValue("stockExercised")),
        filterParams: filterparams,
        menuTabs: ["filterMenuTab"],
        sortable: true,
      },
      {
        headerName: "CASHED OUT",
        field: "cashedOut",
        autoHeight: true,
        wrapText: true,
        filter: "agNumberColumnFilter",
        filterValueGetter: (params) =>
          stringToNumberConvertor(params.getValue("cashedOut")),
        filterParams: filterparams,
        menuTabs: ["filterMenuTab"],
        sortable: true,
      },
      {
        headerName: "TOTAL EXERCISED",
        field: "totalExercised",
        autoHeight: true,
        wrapText: true,
        filter: "agNumberColumnFilter",
        filterValueGetter: (params) =>
          stringToNumberConvertor(params.getValue("totalExercised")),
        filterParams: filterparams,
        menuTabs: ["filterMenuTab"],
        sortable: true,
      },
      {
        headerName: "LAPSED",
        field: "lapsed",
        autoHeight: true,
        wrapText: true,
        filter: "agNumberColumnFilter",
        filterValueGetter: (params) =>
          stringToNumberConvertor(params.getValue("lapsed")),
        menuTabs: ["filterMenuTab"],
        sortable: true,
      },
      {
        headerName: "FORFEITED",
        field: "forfeited",
        autoHeight: true,
        wrapText: true,
        filter: "agNumberColumnFilter",
        filterValueGetter: (params) =>
          stringToNumberConvertor(params.getValue("forfeited")),
        menuTabs: ["filterMenuTab"],
        sortable: true,
      },
      {
        headerName: "GRANT TAGS",
        field: "tags",
        autoHeight: true,
        wrapText: true,
        minWidth: 180,
        filter: "agSetColumnFilter",
        menuTabs: ["filterMenuTab"],
        hide: true,
        sortable: true,
        cellStyle: { "padding-top": "15px" },
        valueGetter: (params: any) =>
          Array.isArray(params.data.tags)
            ? params.data.tags.map((tag: any) => tag.label)
            : [],
        comparator: (valueA: string[] = [], valueB: string[] = []) =>
          (Array.isArray(valueA) ? valueA : [])
            .join(", ")
            .localeCompare((Array.isArray(valueB) ? valueB : []).join(", ")),
        filterParams: {
          suppressSelectAll: false,
          suppressMiniFilter: false,
          buttons: ["reset"],
        },
        cellRenderer: (params: any) => TagsCell({ value: params.data.tags }),
      },

      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        colId: "action-column",
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        resizable: false,
        sortable: false,
        menuTabs: [],
        // cellRendererParams: ({ value }: { value: any }) => value.props,
        cellRendererParams: ({ value }: { value: { props: any } }) =>
          value ? value.props : null,
        cellRenderer: CTADropdown,
      },
    ],
    [showValueAtGrantToAdmin]
  );

  const rowData = useMemo(
    () =>
      tableData?.map((grant: any) => ({
        avatar: (
          <Avatar
            name={grant.optionHolderName}
            status={grant.employmentStatus}
          />
        ),
        holderName: {
          employeeIdentifier: grant.employeeIdentifier,
          optionHolderName: grant.optionHolderName,
          statusId: grant.employmentStatus,
        },
        grantInfo: <GrantInfoCell grant={grant} currency={currency} />,
        grantStatus: grant.optionHolderState.replace("_", " "),

        optionsGranted: (
          <OptionInfoCell
            optionsGranted={grant.optionsGranted}
            planName={grant.planName}
            currency={currency}
            isFractional={grant?.isFractional}
          />
        ),
        valueAtGrant: grant?.sharePrice,
        grantPrice: grant?.grantPrice,
        pricePerOption: grant?.pricePerOption,
        vested: (
          <GrantDetailsCell
            optionsVested={grant.optionsVested}
            vestingTemplateName={grant.vestingTemplateName}
            cliffPeriod={grant.cliffPeriod}
            vestingInterval={grant.vestingInterval}
            vestingPeriod={grant.vestingPeriod}
            vestingTriggerType={grant.vestingTriggerType}
            currency={currency}
            isFractional={true}
          />
        ),
        equivalentSharesGranted:
          (grant?.optionsGranted || 0) * (grant.ratio || 1),
        unvested: getFormattedValue(
          grant?.unVested || 0,
          currency,
          grant.isFractional
        ),
        netVested: getFormattedValue(
          grant?.netVested || 0,
          currency,
          grant?.isFractional
        ),
        stockExercised: getFormattedValue(
          grant?.optionsExercisedWithStock || 0,
          currency,
          grant.isFractional
        ),
        cashedOut: getFormattedValue(
          grant?.optionsExercisedWithCash || 0,
          currency,
          grant.isFractional
        ),
        totalExercised: getFormattedValue(
          grant?.optionsExercised || 0,
          currency,
          grant.isFractional
        ),
        lapsed: getFormattedValue(
          grant.optionsLapsed || 0,
          currency,
          grant.isFractional
        ),
        forfeited: getFormattedValue(
          grant.optionsForfeited || 0,
          currency,
          grant.isFractional
        ),
        tags: grant?.tags || [],
        employeeId: grant.employeeId,
        actions: (
          <CTADropdown
            dropdownClassName=" xl:right-10 lg:right-8 md:right-2"
            actions={[
              {
                name: "Edit Grant",
                disabled:
                  grant.optionHolderState === "GRANTED" ||
                  grant.optionHolderState === "OFFERED" ||
                  grant.optionHolderState === "OFFER_EXPIRED" ||
                  !determineUserAccessToResource(
                    permission?.aclList || [],
                    Resource.Grant,
                    DefaultAction.Edit
                  ) ||
                  grant.isWithdrawLapsed,
              },
              {
                name: "Update grant status",
                disabled:
                  !determineUserAccessToResource(
                    permission?.aclList || [],
                    Resource.Grant,
                    DefaultAction.STATE_CHANGE
                  ) ||
                  !grant.isStateChangeAllowed ||
                  grant.isWithdrawLapsed,
              },
              {
                name: "Clone Grant",
                disabled: !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.Grant,
                  DefaultAction.Create
                ),
              },
              {
                name: "Accelerate Vesting",
                disabled:
                  !isAccelerationAllowed(grant) ||
                  !determineUserAccessToResource(
                    permission?.aclList || [],
                    Resource.All,
                    DefaultAction.All
                  ),
              },
              {
                name: "Exercise Options",
                disabled:
                  !isExerciseAllowed(grant) ||
                  !determineUserAccessToResource(
                    permission?.aclList || [],
                    Resource.All,
                    DefaultAction.All
                  ),
              },
              {
                name: "Extend Exercise",
                disabled:
                  (grant.netVested || 0) <= 0 ||
                  !determineUserAccessToResource(
                    permission?.aclList || [],
                    Resource.All,
                    DefaultAction.All
                  ),
              },
              {
                name: "Manage Grant Docs",
                disabled: !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.Grant,
                  DefaultAction.Read
                ),
              },
              {
                name: "Delete",
                disabled:
                  grant.optionHolderState === "GRANTED" ||
                  grant.optionHolderState === "OFFERED" ||
                  grant.optionHolderState === "RESIGNED" ||
                  !determineUserAccessToResource(
                    permission?.aclList || [],
                    Resource.Grant,
                    DefaultAction.Delete
                  ),
              },
            ]}
            onAction={(action) => handleAction(grant, action)}
          />
        ),
      })),
    [tableData]
  );

  const onPageSizeChanged = useCallback(() => {
    const value = (document.getElementById("page-size") as HTMLInputElement)
      .value;
    gridApi.current?.api.paginationSetPageSize(Number(value));
  }, []);

  const onGridReady = (params: any) => {
    gridApi.current = params;
    gridRef.current = params.api;
    params.columnApi.getAllColumns().map((column: any) => column.colId);
  };

  const [filteredRowData, setFilteredRowData] = useState<any>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    const filterModel = grid.api.getFilterModel();
    const isOnlyEmptySetFilters =
      Object.keys(filterModel).length > 0 &&
      Object.values(filterModel).every(
        (filter: any) =>
          filter.filterType === "set" &&
          Array.isArray(filter.values) &&
          filter.values.length === 0
      );
    setIsFilterApplied(filtersApplied);
    tableFilterCount(filtersApplied);

    const filteredData = grid.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);
    setFilteredRowData(filteredData);
    uploadFilter(filterModel);
    setPinnedBottomRowData(grid);

    try {
      const displayed = (grid.api.getModel().rowsToDisplay || []).length;
      if (!displayed) grid.api.showNoRowsOverlay?.();
      else grid.api.hideOverlay?.();
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    const filterModel = gridApi.current?.api.getFilterModel();

    const filteredData = gridApi.current?.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);

    setFilteredRowData(filteredData);
    gridApi.current?.api.setFilterModel(tableState?.filterState);

    try {
      const displayed = (gridApi.current?.api.getModel().rowsToDisplay || [])
        .length;
      if (!displayed) gridApi.current?.api.showNoRowsOverlay?.();
      else gridApi.current?.api.hideOverlay?.();
    } catch (e) {
      // ignore
    }
  }, [gridApi, tableState?.filterState, tableState?.columnState, tableData]);

  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const saveNewFilter = (filterName: string) => {
    const filterState = gridApi.current?.api.getFilterModel();
    const filterStateToSave: SaveTableState = {
      filterName,
      filterState,
      tableName: "PLANSANDGRANTS",
    };
    saveTableFilterState(filterStateToSave).then((_) => {
      toast(`${filterName} saved successfully`, {
        autoClose: 2000,
        type: "success",
      });
      refetch();
    });
  };
  const onFilterSelected = (filterItem: AvailableTableFilters | null) => {
    gridApi?.current?.api.setFilterModel(filterItem?.filterState);
  };
  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api.openToolPanel(key);
        else gridApi?.current?.api.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) gridApi?.current?.api.openToolPanel(key);
        else gridApi?.current?.api.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  function setPinnedBottomRowData({ api }: { api: any }) {
    const data = api.rowModel.rootNode;
    const isFractional = data.childrenAfterAggFilter.some(
      (d: any) => d.data.grantInfo.props.grant.isFractional
    );

    const filteredData = data.childrenAfterAggFilter.map(
      (element: any) => element.data.grantInfo.props.grant
    );

    const optionsGranted = filteredData?.reduce(
      (accumulator: any, data: any) =>
        accumulator + Number(data.optionsGranted),
      0
    );

    const vested = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + Number(data.optionsVested),
      0
    );

    const unvested = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + Number(data.unVested),
      0
    );
    const netVested = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + Number(data.netVested),
      0
    );
    const stockExercised = filteredData?.reduce(
      (accumulator: any, data: any) =>
        accumulator + Number(data.optionsExercisedWithStock),
      0
    );
    const cashedOut = filteredData?.reduce(
      (accumulator: any, data: any) =>
        accumulator + Number(data.optionsExercisedWithCash),
      0
    );
    const totalExercised = filteredData?.reduce(
      (accumulator: any, data: any) =>
        accumulator + Number(data.optionsExercised),
      0
    );
    const lapsed = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + Number(data.optionsLapsed),
      0
    );
    const forfeited = filteredData?.reduce(
      (accumulator: any, data: any) =>
        accumulator + Number(data.optionsForfeited),
      0
    );
    const valueAtGrant = filteredData?.reduce(
      (accumulator: number, grantData: Grant) =>
        accumulator + Number(grantData.sharePrice || 0),
      0
    );

    api.setPinnedBottomRowData([
      {
        holderName: {
          employeeIdentifier: "",
          optionHolderName: "Total",
          statusId: "",
        },
        optionsGranted: (
          <OptionInfoCell
            optionsGranted={optionsGranted}
            planName={""}
            currency={currency}
            isFractional={isFractional}
          />
        ),
        vested: (
          <GrantDetailsCell
            optionsVested={vested}
            vestingTemplateName=""
            cliffPeriod={0}
            vestingInterval={0}
            vestingPeriod={0}
            vestingTriggerType=""
            currency={currency}
            isFractional={isFractional}
          />
        ),
        unvested: getFormattedValue(unvested, currency, isFractional),
        netVested: getFormattedValue(netVested, currency, isFractional),
        stockExercised: getFormattedValue(
          stockExercised,
          currency,
          isFractional
        ),
        cashedOut: getFormattedValue(cashedOut, currency, isFractional),
        totalExercised: getFormattedValue(
          totalExercised,
          currency,
          isFractional
        ),
        valueAtGrant,
        lapsed: getFormattedValue(lapsed, currency, isFractional),
        forfeited: getFormattedValue(forfeited, currency, isFractional),
        actions: "",
        isLastPinnedRow: true,
      },
    ]);
  }
  return (
    <VStack className="w-full">
      <HStack className="items-center justify-between w-full gap-4">
        <HStack className="items-center gap-4 ">
          <SearchInput
            className=""
            placeholder="Search"
            onChange={(e: any) => {
              gridApi.current.api.setQuickFilter(e.target.value);
            }}
          />
        </HStack>
        <HStack className="items-center justify-end gap-4">
          <HStack className="items-center gap-4">
            <CloudSetting
              tableId={TableId.grantTable}
              isLoading={isSaving}
              filterCondition={filterCondition}
              setFilterCondition={setFilterCondition}
              gridApi={gridApi}
            />
          </HStack>

          {/* <QuickSearch
          className="px-2"
          placeholder="Search"
          onChange={(e) => {
            gridApi.current.setQuickFilter(e.target.value);
          }}
        /> */}
          <IconCTAButton
            value={"Columns"}
            onClick={() => openToolPanel("columns")}
            iconName={"fluent:column-triple-edit-20-regular"}
            className={`px-4 font-medium items-center flex flex-row ${
              isColumnOpen ? "text-orange-501" : "text-gray-400"
            }`}
            selected={isColumnOpen}
          />
          <IconCTAButton
            value={"Filters"}
            onClick={() => openToolPanel("filters")}
            iconName={"material-symbols:filter-alt"}
            className={`px-4  font-medium items-center flex flex-row ${
              isFilterOpen ? "text-orange-501" : "text-gray-400"
            }`}
            selected={isFilterOpen}
          />
          {/* <MyCustomFilters
            currentFilterState={gridApi.current?.api.getFilterModel()}
            availableFilters={data || []}
            onFilterSelect={onFilterSelected}
            className={""}
            onNewFilterAddition={(e) => {
              saveNewFilter(e);
            }}
          ></MyCustomFilters> */}
          <AdvancedAgGridSearch
            gridApi={gridRef?.current}
            tableIdentifier="grantTable"
          />
        </HStack>
        {/* <div
            className={`pr-2 ${
              isColumnOpen ? "text-orange-500" : "text-black"
            }`}
          >
            <button className="px-4" onClick={() => openToolPanel("columns")}>
              Columns
            </button>
          </div>
          <div
            className={`pr-2 ${
              isFilterOpen ? "text-orange-500" : "text-black"
            }`}
          >
            <button className="px-4" onClick={() => openToolPanel("filters")}>
              Filters
            </button>
          </div> */}
      </HStack>
      <HStack className="justify-between w-full">
        <Box
          style={{
            height: `${Math.max(
              400,
              (rowData.length >= 10 ? 10 : rowData.length + 3) * 60
            )}px`,
          }}
          className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
        >
          <AgGridReact
            getRowStyle={(params) => ({
              background: params.data.isLastPinnedRow ? "#f0f0f0" : "",
              fontWeight: params.data.isLastPinnedRow ? "normal" : "normal",
              color: params.data.isLastPinnedRow ? "black" : "",
            })}
            onFilterChanged={onAgGridFilterChanged}
            onRowDataUpdated={setPinnedBottomRowData}
            alwaysShowHorizontalScroll
            suppressHorizontalScroll={true}
            alwaysMultiSort
            defaultColDef={defaultColDef}
            suppressRowTransform={true}
            rowData={rowData}
            animateRows={true}
            pagination={true}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            components={componentsRegistery}
            onCellClicked={handleCellClick}
            suppressMenuHide={true}
            rowClass={"border-t border-dashed cursor-pointer"}
            rowSelection="multiple"
            sideBar={sideBar}
            rowMultiSelectWithClick={true}
            onSelectionChanged={handleSelectionChanged}
            onGridReady={onGridReady}
            gridOptions={gridOptions}
            suppressRowClickSelection={true}
            columnDefs={columnDefs}
            onColumnEverythingChanged={uploadColumn}
            onColumnResized={uploadColumn}
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
            }
          ></AgGridReact>
        </Box>
      </HStack>
    </VStack>
  );
}

function HolderInfoRender(props: any) {
  const isTotal = props.value.optionHolderName === "Total";
  return (
    <HStack
      className={`px-2 ${
        isTotal ? "justify-center items-center" : "align-top"
      }`}
    >
      {isTotal ? null : (
        <Avatar
          name={props.value.optionHolderName}
          status={props.value.statusId}
        />
      )}
      <Box className="px-2 align-center">
        <p className={`text-xs  font-medium text-gray-dark`}>
          {limitString(props.value.optionHolderName, 40)}
        </p>
        <p className={`text-xxs py-1  text-gray-light `}>
          {props.value.employeeIdentifier}
        </p>
      </Box>
    </HStack>
  );
}

const ValueAtGrantCell: React.FC<{
  sharePrice?: number;
  grantPrice?: number;
  currency: string;
}> = ({ sharePrice, grantPrice, currency }) => (
  <HStack className="align-center">
    <Box>
      <p className="text-xs font-normal text-gray-dark">
        {formatCurrency(sharePrice || 0, currency)}
      </p>
      {grantPrice != null && (
        <p className="text-xxs text-gray-light">
          Fmv: {formatCurrency(grantPrice, currency)}
        </p>
      )}
    </Box>
  </HStack>
);

const OptionInfoCell: React.FC<{
  optionsGranted: number;
  planName: string;
  currency: string;
  isFractional: boolean;
}> = ({ optionsGranted, planName, currency, isFractional }) => (
  <HStack className="align-center">
    <Box className="">
      <p className={`text-xs font-normal text-gray-dark`}>
        {getFormattedValue(optionsGranted, currency, isFractional)}
      </p>
      <p className=" text-xxs text-gray-light">{planName}</p>
    </Box>
  </HStack>
);

const GrantInfoCell: React.FC<GrantInfoCellProps> = ({ grant, currency }) => {
  // Check if grant is defined before accessing its properties
  if (!grant || typeof grant.grantDate === "undefined") {
    return null; // or some default content, or an error message
  }
  const formattedDate = new Date(grant.grantDate).toUTCString().slice(5, 17);

  const { color, backgroundColor } = StatusLabelColors.PENDING;
  return (
    <VStack className="px-2 text-xs font-medium align-top text-gray-dark ">
      <HStack className="">
        <VStack className="text-ellipsis">{grant.grantIdentifier}</VStack>
      </HStack>
      <HStack></HStack>
      <HStack className="leading-5 text-xxs text-gray-light">
        {formattedDate} | Price: {formatCurrency(grant.grantPrice, currency)}
      </HStack>
      {grant.isPausedVesting && grant.pausedVestingDate && (
        <span
          className={`flex items-center capitalize min-w-10 h-6  rounded-sm font-medium text-center text-[9px]  leading-4 px-1 whitespace-nowrap w-fit mb-1`}
          style={{ color, backgroundColor }}
        >
          Paused On {formatDisplayDate(grant.pausedVestingDate)}
        </span>
      )}
    </VStack>
  );
};

const GrantStatus = (grant: any) => (
  <HStack className="flex py-4">
    <VStack className="items-center justify-center">
      {grant.value && (
        <GrantStatusLabel
          _className={`flex items-center justify-center text-center capitalize min-w-20 h-6 rounded-sm font-medium text-[9px] leading-4 px-2`}
          state={grant.value}
        />
      )}
    </VStack>
  </HStack>
);

const GrantDetailsCell: React.FC<{
  optionsVested: number;
  vestingTemplateName: string;
  cliffPeriod: number;
  vestingInterval: number | undefined;
  vestingPeriod: number | undefined;
  vestingTriggerType: string;
  currency: string;
  isFractional: boolean;
}> = ({
  optionsVested,
  vestingTemplateName,
  cliffPeriod,
  vestingInterval,
  vestingPeriod,
  vestingTriggerType,
  currency,
  isFractional,
}) => (
  <HStack className="px-2 py-4 align-top">
    <Box>
      <p className="text-xs font-normal text-gray-dark">
        {getFormattedValue(optionsVested, currency, isFractional)}
      </p>
      {vestingTemplateName && (
        <p className=" text-xxs text-gray-light">
          {vestingTemplateName} Cliff: {cliffPeriod}
          {vestingInterval && vestingTriggerType === TriggerType.TIME
            ? `, ${vestingInterval}/${vestingPeriod})`
            : vestingTriggerType === TriggerType.EVENT
            ? `, Event Based Vesting)`
            : `, Custom Vesting Schedule)`}
        </p>
      )}
    </Box>
  </HStack>
);

export default forwardRef(AgGridGrantsTable);
